import { useMemo, useState, type ReactNode } from "react";
import { Modal, Typography, Progress, Alert, Empty, Divider, Button, Segmented } from "antd";
import { TbArrowUp, TbArrowDown, TbCheck } from "react-icons/tb";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";

import { T_MergedTransaction } from "dtos/loan-transaction.dto";
import { FREQUENCY_TYPES, LoanSchemaDto } from "dtos/loan.dto";
import { FileSchemaDto } from "dtos/file.dto";
import { T_Populate } from "types/_utils.types";
import { formatMoney, round } from "utils/functions";
import { monthKey, formatMonthLabel } from "components/Application/LoanPaymentTimeLine";
import { FrequencyLabel } from "components/Application/constants";

const { Text } = Typography;

export type T_InsightLoan = T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>;

type T_PaybackStatus = "unknown" | "underpaid" | "overpaid" | "paid";

type T_PaybackProgress = {
  status: T_PaybackStatus;
  paybackAmount?: number;
  acceptedTotal: number;
  remaining?: number;
  progressPct?: number;
};

type T_InstallmentPace = {
  available: boolean;
  missing: string[];
  installmentAmount?: number;
  periodDays?: number;
  daysSinceFunded?: number;
  expectedInstallmentsElapsed?: number;
  expectedAmountByNow?: number;
  paceGap?: number; // expectedAmountByNow - acceptedTotal; positive = behind, negative = ahead
  installmentsMatchedByAmount?: number;
};

type T_MonthlyTotal = { key: string; label: string; total: number };
type T_DailyTotal = { date: string; label: string; total: number };

type T_TransactionTrend = {
  daily: T_DailyTotal[];
  monthly: T_MonthlyTotal[];
  trend: "increasing" | "decreasing" | "flat" | "not-enough-data";
  longestGapDays?: number;
  daysSinceLastTransaction?: number;
  isStale: boolean;
  frequency?: FREQUENCY_TYPES;
  periodsOverdue?: number;
};

type T_DueReconciliation = {
  available: boolean;
  systemDue?: number;
  computedDue?: number;
  discrepancy?: number;
  matches?: boolean;
};

export type T_LoanInsights = {
  acceptedTotal: number;
  pendingCount: number;
  pendingTotal: number;
  paybackProgress: T_PaybackProgress;
  installmentPace: T_InstallmentPace;
  transactionTrend: T_TransactionTrend;
  dueReconciliation: T_DueReconciliation;
};

const PERIOD_DAYS: Record<FREQUENCY_TYPES, number> = {
  [FREQUENCY_TYPES.DAILY]: 1,
  [FREQUENCY_TYPES.WEEKLY]: 7,
  [FREQUENCY_TYPES.MONTHLY]: 30.44,
  [FREQUENCY_TYPES.YEARLY]: 365.25,
};

const AMOUNT_EPSILON = 0.5;

function collectedAmount(items: T_MergedTransaction[]): number {
  return -items.reduce((sum, t) => sum + t.amount, 0);
}

export function computeLoanInsights(
  loan: T_InsightLoan | undefined,
  acceptedItems: T_MergedTransaction[],
  pendingItems: T_MergedTransaction[],
): T_LoanInsights {
  const acceptedTotal = collectedAmount(acceptedItems);
  const pendingTotal = collectedAmount(pendingItems);

  // 1. Payback progress
  const paybackAmount = loan?.payback_amount;
  const paybackProgress: T_PaybackProgress =
    paybackAmount === undefined
      ? { status: "unknown", acceptedTotal }
      : (() => {
          const remaining = paybackAmount - acceptedTotal;
          const status: T_PaybackStatus =
            Math.abs(remaining) <= AMOUNT_EPSILON ? "paid" : remaining > 0 ? "underpaid" : "overpaid";
          return {
            status,
            paybackAmount,
            acceptedTotal,
            remaining,
            progressPct: paybackAmount > 0 ? (acceptedTotal / paybackAmount) * 100 : undefined,
          };
        })();

  const frequencyPeriodDays = loan?.frequency ? PERIOD_DAYS[loan.frequency] : undefined;

  const missing: string[] = [];
  if (!loan?.payments) missing.push("installment amount");
  if (!loan?.tenure) missing.push("tenure");
  if (!loan?.frequency) missing.push("frequency");
  if (!loan?.funded_date) missing.push("funded date");
  if (!paybackAmount) missing.push("payback amount");

  const installmentPace: T_InstallmentPace =
    missing.length > 0 || !loan
      ? { available: false, missing }
      : (() => {
          const installmentAmount = loan.payments!;
          const periodDays = frequencyPeriodDays!;
          const daysSinceFunded = Math.max(dayjs().diff(dayjs(loan.funded_date), "day"), 0);
          const expectedInstallmentsElapsed = Math.min(Math.floor(daysSinceFunded / periodDays), loan.tenure);
          const expectedAmountByNow = Math.min(expectedInstallmentsElapsed * installmentAmount, paybackAmount!);
          const paceGap = expectedAmountByNow - acceptedTotal;
          const tolerance = installmentAmount * 0.1;
          const installmentsMatchedByAmount = acceptedItems.filter(
            (t) => Math.abs(Math.abs(t.amount) - installmentAmount) <= tolerance,
          ).length;

          return {
            available: true,
            missing: [],
            installmentAmount,
            periodDays,
            daysSinceFunded,
            expectedInstallmentsElapsed,
            expectedAmountByNow,
            paceGap,
            installmentsMatchedByAmount,
          };
        })();

  // 3. Transaction trend
  const monthlyMap = new Map<string, number>();
  const dailyMap = new Map<string, number>();
  for (const t of acceptedItems) {
    const monthlyTotalKey = monthKey(t.transaction_date);
    monthlyMap.set(monthlyTotalKey, (monthlyMap.get(monthlyTotalKey) ?? 0) + collectedAmount([t]));
    dailyMap.set(t.transaction_date, (dailyMap.get(t.transaction_date) ?? 0) + collectedAmount([t]));
  }
  const monthly: T_MonthlyTotal[] = Array.from(monthlyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, total]) => ({ key, label: formatMonthLabel(`${key}-01`), total }));
  const daily: T_DailyTotal[] = Array.from(dailyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, total]) => ({ date, label: dayjs(date).format("MMM D, YYYY"), total }));

  let trend: T_TransactionTrend["trend"] = "not-enough-data";
  if (monthly.length >= 2) {
    const recentWindow = monthly.slice(-3);
    const priorWindow = monthly.slice(-6, -3);
    if (priorWindow.length > 0) {
      const recentAvg = recentWindow.reduce((s, m) => s + m.total, 0) / recentWindow.length;
      const priorAvg = priorWindow.reduce((s, m) => s + m.total, 0) / priorWindow.length;
      const diffPct = priorAvg !== 0 ? (recentAvg - priorAvg) / Math.abs(priorAvg) : recentAvg > 0 ? 1 : 0;
      trend = Math.abs(diffPct) < 0.05 ? "flat" : diffPct > 0 ? "increasing" : "decreasing";
    }
  }

  const sortedDates = acceptedItems.map((t) => dayjs(t.transaction_date)).sort((a, b) => a.valueOf() - b.valueOf());

  let longestGapDays: number | undefined;
  for (let i = 1; i < sortedDates.length; i++) {
    const gap = sortedDates[i].diff(sortedDates[i - 1], "day");
    if (longestGapDays === undefined || gap > longestGapDays) longestGapDays = gap;
  }

  const daysSinceLastTransaction =
    sortedDates.length > 0 ? dayjs().diff(sortedDates[sortedDates.length - 1], "day") : undefined;

  const staleThreshold = frequencyPeriodDays ?? 30;
  const isStale = daysSinceLastTransaction !== undefined && daysSinceLastTransaction > staleThreshold;
  const periodsOverdue =
    frequencyPeriodDays && daysSinceLastTransaction !== undefined
      ? Math.floor(daysSinceLastTransaction / frequencyPeriodDays)
      : undefined;

  const transactionTrend: T_TransactionTrend = {
    daily,
    monthly,
    trend,
    longestGapDays,
    daysSinceLastTransaction,
    isStale,
    frequency: loan?.frequency,
    periodsOverdue,
  };

  // 4. Due-amount reconciliation
  const dueReconciliation: T_DueReconciliation =
    loan?.due_amount === undefined || paybackAmount === undefined
      ? { available: false }
      : (() => {
          const computedDue = paybackAmount - acceptedTotal;
          const discrepancy = computedDue - loan.due_amount!;
          return {
            available: true,
            systemDue: loan.due_amount,
            computedDue,
            discrepancy,
            matches: Math.abs(discrepancy) <= 1,
          };
        })();

  return {
    acceptedTotal,
    pendingCount: pendingItems.length,
    pendingTotal,
    paybackProgress,
    installmentPace,
    transactionTrend,
    dueReconciliation,
  };
}

const STATUS_COLOR = { over: "#168F5A", under: "#C73E45", exact: "#7c3aed", unknown: "#a1a1aa" };

function Figure({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: string }) {
  return (
    <div style={{ background: "#FAFAFA", borderRadius: 10, padding: "11px 13px" }}>
      <Text style={{ fontSize: 12, color: "#A1A1AA", display: "block" }}>{label}</Text>
      <Text
        style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: tone ?? "#18181B" }}
        className="block"
      >
        {value}
      </Text>
      {hint && (
        <Text style={{ fontSize: 11, color: "#A1A1AA" }} className="block">
          {hint}
        </Text>
      )}
    </div>
  );
}

function Section({ title, extra, children }: { title: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Text
          style={{ fontSize: 11, letterSpacing: "0.08em", color: "#71717A", fontWeight: 600 }}
          className="uppercase"
        >
          {title}
        </Text>
        {extra}
      </div>
      {children}
    </div>
  );
}

function PaybackProgressSection({
  progress,
  pendingCount,
  pendingTotal,
}: {
  progress: T_PaybackProgress;
  pendingCount: number;
  pendingTotal: number;
}) {
  if (progress.status === "unknown") {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Payback amount isn't set on this loan" />;
  }

  const color =
    progress.status === "overpaid"
      ? STATUS_COLOR.over
      : progress.status === "underpaid"
        ? STATUS_COLOR.under
        : STATUS_COLOR.exact;
  const Icon = progress.status === "overpaid" ? TbArrowUp : progress.status === "underpaid" ? TbArrowDown : TbCheck;
  const pct = Math.min(Math.max(progress.progressPct ?? 0, 0), 100);
  const statusText =
    progress.status === "paid"
      ? "Fully paid"
      : progress.status === "overpaid"
        ? `Overpaid by ${formatMoney(Math.abs(progress.remaining!))}`
        : `${formatMoney(progress.remaining!)} remaining`;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        <Figure label="Payback amount" value={formatMoney(progress.paybackAmount!)} />
        <Figure label="Accepted total" value={formatMoney(progress.acceptedTotal)} />
        <Figure
          label={progress.status === "overpaid" ? "Overpaid by" : "Remaining"}
          value={formatMoney(Math.abs(progress.remaining!))}
          tone={color}
        />
      </div>
      <Progress percent={pct} showInfo={false} strokeColor={color} trailColor="#F4F4F5" className="!mb-0" />
      <div className="flex items-center gap-1.5 flex-wrap">
        <Icon size={14} color={color} className="stroke-[3]" />
        <Text style={{ fontSize: 12, fontWeight: 600, color }}>{statusText}</Text>
        <Text style={{ fontSize: 12, color: "#A1A1AA" }}>
          · {round(progress.progressPct ?? 0, 1)}% of payback collected
        </Text>
      </div>
      {pendingCount > 0 && (
        <Text style={{ fontSize: 11, color: "#A1A1AA" }}>
          +{pendingCount} pending transaction{pendingCount === 1 ? "" : "s"} worth {formatMoney(pendingTotal)} not yet
          counted
        </Text>
      )}
    </div>
  );
}

function InstallmentPaceSection({ pace }: { pace: T_InstallmentPace }) {
  if (!pace.available) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={`Not enough loan data to compute pace (missing ${pace.missing.join(", ")})`}
      />
    );
  }

  const behind = pace.paceGap! > AMOUNT_EPSILON;
  const ahead = pace.paceGap! < -AMOUNT_EPSILON;
  const color = behind ? STATUS_COLOR.under : ahead ? STATUS_COLOR.over : STATUS_COLOR.exact;
  const paceText = behind
    ? `Behind by ${formatMoney(pace.paceGap!)}`
    : ahead
      ? `Ahead by ${formatMoney(Math.abs(pace.paceGap!))}`
      : "On pace";

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        <Figure label="Installment amount" value={formatMoney(pace.installmentAmount!)} />
        <Figure
          label="Expected by now"
          value={formatMoney(pace.expectedAmountByNow!)}
          hint={`${pace.expectedInstallmentsElapsed} installment${pace.expectedInstallmentsElapsed === 1 ? "" : "s"} elapsed`}
        />
        <Figure label="Pace" value={paceText} tone={color} />
      </div>
      <Text style={{ fontSize: 12, color: "#71717A" }}>
        {pace.installmentsMatchedByAmount} accepted transaction{pace.installmentsMatchedByAmount === 1 ? "" : "s"} match
        the installment amount (±10%)
      </Text>
    </div>
  );
}

function pickTicks(labels: string[], maxTicks: number): string[] {
  if (labels.length <= maxTicks) return labels;
  const result: string[] = [labels[0]];
  const step = (labels.length - 1) / (maxTicks - 1);
  for (let i = 1; i < maxTicks - 1; i++) {
    result.push(labels[Math.round(i * step)]);
  }
  result.push(labels[labels.length - 1]);
  return result;
}

function TrendChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: { label: string } }>;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: "#374151" }}>{payload[0].payload.label}</div>
      <div style={{ color: STATUS_COLOR.over, fontWeight: 500 }}>{formatMoney(payload[0].value)}</div>
    </div>
  );
}

function TransactionTrendSection({
  trend,
  granularity,
}: {
  trend: T_TransactionTrend;
  granularity: "daily" | "monthly";
}) {
  if (trend.monthly.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No accepted transactions yet" />;
  }

  const TrendIcon = trend.trend === "increasing" ? TbArrowUp : trend.trend === "decreasing" ? TbArrowDown : TbCheck;
  const trendColor =
    trend.trend === "increasing" ? STATUS_COLOR.over : trend.trend === "decreasing" ? STATUS_COLOR.under : "#71717A";
  const trendLabel =
    trend.trend === "not-enough-data" ? "Not enough history to judge a trend" : `Collections are ${trend.trend}`;

  const chartData = granularity === "daily" ? trend.daily : trend.monthly;
  const ticks = pickTicks(
    chartData.map((d) => d.label),
    granularity === "daily" ? 6 : 8,
  );

  const staleMessage = (() => {
    if (!trend.isStale) return undefined;
    if (!trend.frequency || trend.periodsOverdue === undefined) {
      return `No collected transaction in ${trend.daysSinceLastTransaction}d. Either a payment hasn't been collected yet, or the bank statement hasn't been updated.`;
    }
    const unit = FrequencyLabel[trend.frequency];
    const unitLabel = trend.periodsOverdue === 1 ? unit.slice(0, -1) : unit;
    return `Payments are collected ${trend.frequency}, but it's been ${trend.daysSinceLastTransaction}d since the last one — more than ${trend.periodsOverdue} ${unitLabel} overdue. Either a payment hasn't been collected yet, or the bank statement hasn't been updated.`;
  })();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1.5">
        <TrendIcon size={14} color={trendColor} className="stroke-[3]" />
        <Text style={{ fontSize: 12, fontWeight: 600, color: trendColor }}>{trendLabel}</Text>
      </div>

      <ResponsiveContainer width="100%" height={190}>
        {granularity === "daily" ? (
          <LineChart data={trend.daily} margin={{ top: 4, right: 8, left: -16, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" />
            <XAxis
              dataKey="label"
              ticks={ticks}
              interval={0}
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              angle={-40}
              textAnchor="end"
              axisLine={false}
              tickLine={false}
              height={36}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              tickFormatter={(v) => formatMoney(v)}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip content={<TrendChartTooltip />} />
            <Line
              type="monotone"
              dataKey="total"
              stroke={STATUS_COLOR.over}
              strokeWidth={1.5}
              dot={{ r: 2, fill: STATUS_COLOR.over }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        ) : (
          <BarChart data={trend.monthly} margin={{ top: 4, right: 8, left: -16, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" vertical={false} />
            <XAxis
              dataKey="label"
              ticks={ticks}
              interval={0}
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              angle={-40}
              textAnchor="end"
              axisLine={false}
              tickLine={false}
              height={36}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#A1A1AA" }}
              tickFormatter={(v) => formatMoney(v)}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip content={<TrendChartTooltip />} />
            <Bar dataKey="total" fill={STATUS_COLOR.over} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        )}
      </ResponsiveContainer>

      <div className="grid grid-cols-2 gap-3">
        <Figure label="Longest gap" value={trend.longestGapDays !== undefined ? `${trend.longestGapDays}d` : "—"} />
        <Figure
          label="Since last payment"
          value={trend.daysSinceLastTransaction !== undefined ? `${trend.daysSinceLastTransaction}d` : "—"}
          tone={trend.isStale ? STATUS_COLOR.under : undefined}
        />
      </div>

      {staleMessage && <Alert type="warning" showIcon message="Possible missed payment" description={staleMessage} />}
    </div>
  );
}

function DueReconciliationSection({ due }: { due: T_DueReconciliation }) {
  if (!due.available) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recorded balance amount on this loan" />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Figure label="System balance amount" value={formatMoney(due.systemDue!)} />
        <Figure label="Bank-based balance (computed)" value={formatMoney(due.computedDue!)} />
      </div>
      {due.matches ? (
        <Alert type="success" showIcon message="Matches the system's recorded balance amount" />
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Balance amount mismatch"
          description={`The bank-based calculation differs from the recorded balance amount by ${formatMoney(
            Math.abs(due.discrepancy!),
          )}.`}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function LoanPaymentInsightsModal({
  open,
  onClose,
  loan,
  acceptedItems,
  pendingItems,
}: {
  open: boolean;
  onClose: () => void;
  loan: T_InsightLoan | undefined;
  acceptedItems: T_MergedTransaction[];
  pendingItems: T_MergedTransaction[];
}) {
  const insights = useMemo(
    () => computeLoanInsights(loan, acceptedItems, pendingItems),
    [loan, acceptedItems, pendingItems],
  );
  const [trendGranularity, setTrendGranularity] = useState<"daily" | "monthly">("daily");

  return (
    <Modal
      title={
        <div className="flex flex-col">
          <Text style={{ fontSize: 16, fontWeight: 600 }}>Payment insights</Text>
          {loan?.creditor_name && (
            <Text style={{ fontSize: 12, color: "#71717A", fontWeight: 400 }}>{loan.creditor_name}</Text>
          )}
        </div>
      }
      open={open}
      centered
      styles={{
        body: {
          height: 600,
          overflowY: "auto",
        },
      }}
      onCancel={onClose}
      width={680}
      destroyOnClose
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="flex flex-col gap-5 py-2">
        <Section title="Payback progress">
          <PaybackProgressSection
            progress={insights.paybackProgress}
            pendingCount={insights.pendingCount}
            pendingTotal={insights.pendingTotal}
          />
        </Section>

        <Divider style={{ margin: 0 }} />

        {/* <Section title="Installment pace">
          <InstallmentPaceSection pace={insights.installmentPace} />
        </Section> */}
        <Section title="balance amount check">
          <DueReconciliationSection due={insights.dueReconciliation} />
        </Section>

        <Divider style={{ margin: 0 }} />

        <Section
          title="Transaction trend"
          extra={
            insights.transactionTrend.monthly.length > 0 && (
              <Segmented
                size="small"
                value={trendGranularity}
                onChange={(v) => setTrendGranularity(v as "daily" | "monthly")}
                options={[
                  { label: "Daily", value: "daily" },
                  { label: "Monthly", value: "monthly" },
                ]}
              />
            )
          }
        >
          <TransactionTrendSection trend={insights.transactionTrend} granularity={trendGranularity} />
        </Section>

        <Divider style={{ margin: 0 }} />
      </div>
    </Modal>
  );
}
